<script setup>
// W2 — botão de reagir que aparece ao passar o mouse na mensagem.
//
// Abre uma régua com as reações do WhatsApp (as mesmas seis do app) em vez do
// picker completo: reagir é gesto rápido, e uma busca de emoji no meio do fio
// atrapalha mais do que ajuda. Quem quiser outro emoji reage pelo celular.
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  // Emoji com que a equipe já reagiu, para marcar o selecionado.
  selected: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  // Classes de hover revelam o gatilho; o pai (Message.vue) e o
  // `group/msg` controlam quando some. Vazio = sempre visível.
  hoverRevealClass: { type: String, default: '' },
  forceVisible: { type: Boolean, default: false },
});

const emit = defineEmits(['select']);

const { t } = useI18n();

const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

const isOpen = ref(false);
const root = ref(null);

const toggle = () => {
  if (props.disabled) return;
  isOpen.value = !isOpen.value;
};

const choose = emoji => {
  isOpen.value = false;
  emit('select', emoji);
};

// Fecha ao clicar fora e no Esc — a régua flutua sobre o fio e ficaria presa.
const onDocPointerDown = event => {
  if (!isOpen.value) return;
  if (!root.value?.contains(event.target)) isOpen.value = false;
};
const onKeydown = event => {
  if (event.key === 'Escape' && isOpen.value) isOpen.value = false;
};

onMounted(() => {
  document.addEventListener('pointerdown', onDocPointerDown);
  document.addEventListener('keydown', onKeydown);
});
onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onDocPointerDown);
  document.removeEventListener('keydown', onKeydown);
});

const buttonLabel = computed(() => t('CONVERSATION.REACTIONS.ADD'));
</script>

<template>
  <div ref="root" class="relative">
    <button
      type="button"
      :disabled="disabled"
      :aria-label="buttonLabel"
      :aria-expanded="isOpen"
      :title="buttonLabel"
      data-testid="message-reaction-trigger"
      class="flex size-9 shrink-0 items-center justify-center rounded-full border border-n-weak bg-n-solid-2 text-n-slate-11 transition-opacity duration-150 hover:bg-n-alpha-2 hover:text-n-slate-12 disabled:cursor-not-allowed"
      :class="[
        hoverRevealClass,
        {
          '!opacity-100 pointer-events-auto': isOpen || forceVisible,
        },
      ]"
      @click.stop="toggle"
    >
      <span class="i-ph-smiley size-5" />
    </button>

    <div
      v-if="isOpen"
      data-testid="message-reaction-menu"
      role="menu"
      class="reaction-rail absolute bottom-full z-30 mb-1.5 flex items-center gap-0.5 rounded-full border border-n-weak bg-n-solid-2 px-1.5 py-1 shadow-lg ltr:left-0 rtl:right-0"
    >
      <button
        v-for="emoji in QUICK_REACTIONS"
        :key="emoji"
        type="button"
        role="menuitem"
        :aria-label="emoji"
        :aria-pressed="selected === emoji"
        class="reaction-option flex size-9 items-center justify-center rounded-full text-[20px] leading-none transition-transform hover:scale-125"
        :class="{ 'bg-n-blue-3': selected === emoji }"
        @click.stop="choose(emoji)"
      >
        {{ emoji }}
      </button>
    </div>
  </div>
</template>

<style scoped>
@keyframes rail-in {
  from {
    opacity: 0;
    transform: translateY(4px) scale(0.96);
  }
}
@keyframes option-in {
  from {
    opacity: 0;
    transform: scale(0.6);
  }
}
.reaction-rail {
  animation: rail-in 0.16s cubic-bezier(0.2, 0.7, 0.3, 1) both;
}
/* Os emojis entram em cascata, como a régua do WhatsApp. */
.reaction-option {
  animation: option-in 0.18s cubic-bezier(0.2, 0.7, 0.3, 1) both;
}
.reaction-option:nth-child(2) {
  animation-delay: 0.03s;
}
.reaction-option:nth-child(3) {
  animation-delay: 0.06s;
}
.reaction-option:nth-child(4) {
  animation-delay: 0.09s;
}
.reaction-option:nth-child(5) {
  animation-delay: 0.12s;
}
.reaction-option:nth-child(6) {
  animation-delay: 0.15s;
}
@media (prefers-reduced-motion: reduce) {
  .reaction-rail,
  .reaction-option {
    animation: none;
  }
}
</style>
